import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import IdleWarning from "@/components/IdleWarning";
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";
const Explorer = lazy(() => import("@/pages/Explorer"));
const Events = lazy(() => import("@/pages/Events"));
const EventDetail = lazy(() => import("@/pages/EventDetail"));
const Establishments = lazy(() => import("@/pages/Establishments"));
const EstablishmentDetail = lazy(() => import("@/pages/EstablishmentDetail"));
const Live = lazy(() => import("@/pages/Live"));
const LiveDetail = lazy(() => import("@/pages/LiveDetail"));
const SearchResults = lazy(() => import("@/pages/SearchResults"));
const Auth = lazy(() => import("@/pages/Auth"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const PaystackCallback = lazy(() => import("@/pages/PaystackCallback"));
const MyTickets = lazy(() => import("@/pages/MyTickets"));
const BusinessDashboard = lazy(() => import("@/pages/business/BusinessDashboard"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Ville = lazy(() => import("@/pages/Ville"));
const MyClaims = lazy(() => import("@/pages/MyClaims"));
const ResidencesHome = lazy(() => import("@/pages/residences/ResidencesHome"));
const ResidenceSearch = lazy(() => import("@/pages/residences/ResidenceSearch"));
const ResidenceDetail = lazy(() => import("@/pages/residences/ResidenceDetail"));
const HostDashboard = lazy(() => import("@/pages/residences/HostDashboard"));
const MyBookings = lazy(() => import("@/pages/residences/MyBookings"));
const TrackBooking = lazy(() => import("@/pages/residences/TrackBooking"));

const PageLoader = () => (
  <div data-testid="page-loader" className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 rounded-full border-4 border-[#38B5D8]/30 border-t-[#FF5A4F] animate-spin" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/explorer" element={<Explorer />} />
      <Route path="/evenements" element={<Events />} />
      <Route path="/evenement/:slug" element={<EventDetail />} />
      <Route path="/etablissements" element={<Establishments />} />
      <Route path="/etablissement/:slug" element={<EstablishmentDetail />} />
      <Route path="/commune/:commune" element={<Establishments />} />
      <Route path="/ville/:city" element={<Ville />} />
      <Route path="/live" element={<Live />} />
      <Route path="/live/:id" element={<LiveDetail />} />
      <Route path="/recherche" element={<SearchResults />} />
      <Route path="/connexion" element={<Auth />} />
      <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
      <Route path="/reinitialiser" element={<ResetPassword />} />
      <Route path="/favoris" element={<Favorites />} />
      <Route path="/mes-billets" element={<MyTickets />} />
      <Route path="/mes-revendications" element={<MyClaims />} />
      <Route path="/tarifs" element={<Pricing />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/residences" element={<ResidencesHome />} />
      <Route path="/residences/recherche" element={<ResidenceSearch />} />
      <Route path="/residences/ville/:city" element={<ResidenceSearch />} />
      <Route path="/residence/:slug" element={<ResidenceDetail />} />
      <Route path="/mon-espace-hote" element={<HostDashboard />} />
      <Route path="/mes-reservations" element={<MyBookings />} />
      <Route path="/reservation/suivi/:token" element={<TrackBooking />} />
      <Route path="/business" element={<BusinessDashboard />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/paiement/succes" element={<PaymentSuccess />} />
      <Route path="/paiement/annule" element={<PaymentCancel />} />
      <Route path="/paiement/callback" element={<PaystackCallback />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-center" richColors />
        <AppRouter />
        <InstallPrompt />
        <IdleWarning />
      </BrowserRouter>
    </AuthProvider>
  );
}
